import React, { useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { DEFAULT_DATE_FORMAT } from '../../constants/dateFormatConstants';
import {
  useDateDispatch,
  useDateSelector,
} from '../../context/dateContext';
import styles from './CalendarDay.scss';
import { onCalendarDateSelected } from '../../ducks/dateDuck';
import {
  getCalendarDataForSpecificDate, getEndDate, getIsMinStayVisible, getStartDate,
} from '../../selectors/dateSelectors';
import CalendarDayMinNightsTooltip from './CalendarDayMinNightsTooltip';

const CalendarDay = ({
  year, month, day,
}) => {
  const dayRef = useRef();
  const stringDateFormat = useMemo(
    () => DateTime.local(year, month, day).toFormat(DEFAULT_DATE_FORMAT), [year, month, day]);
  const dispatch = useDateDispatch();

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const isMinStayVisible = useDateSelector(getIsMinStayVisible);

  const {
    isEnabled,
    isAvailable,
    isBridging,
    isSelected,
    price: priceForThisNight,
  } = useDateSelector(getCalendarDataForSpecificDate, stringDateFormat);
  const startDate = useDateSelector(getStartDate);
  const endDate = useDateSelector(getEndDate);

  const handleOnClick = () => {
    if (isMinStayVisible) {
      setIsTooltipOpen(true);
      clearTimeout(dayRef.current);
      dayRef.current = setTimeout(() => {
        setIsTooltipOpen(false);
      }, 2000);
    }

    if (isEnabled && isAvailable) {
      dispatch(onCalendarDateSelected(stringDateFormat));
    }
  };

  const calendarDayBorderStyle = () => {
    if (startDate === endDate || !startDate || !endDate) {
      return styles.singleDate;
    }
    if (isSelected && stringDateFormat === startDate) {
      return styles.activeStartDate;
    }
    if (isSelected && stringDateFormat === endDate) {
      return styles.activeEndDate;
    }
    return null;
  };

  return (
    <CalendarDayMinNightsTooltip
      year={year}
      month={month}
      day={day}
      isTooltipOpen={isTooltipOpen}
      setIsTooltipOpen={setIsTooltipOpen}
      stringDateFormat={stringDateFormat}
    >
      <div
        role="button"
        tabIndex="-1"
        onClick={handleOnClick}
        onKeyDown={handleOnClick}
        aria-label={`${month}/${day}/${year}`}
        aria-disabled={(!isEnabled || !isAvailable) && !isSelected}
        data-testid={day}
        className={clsx(
          calendarDayBorderStyle(),
          styles.calendarDay,
          `automation-date-picker-day-${year}-${month}-${day}`,
          {
            [`${styles.active} automation-selected`]: isSelected,
            [`${styles.bridging} automation-bridging`]: isBridging,
          })}
        ref={dayRef}
      >
        <div className="text-wrap text-align-center width-full">
          <div
            className={clsx({
              [`${styles.calDisabled} automation-disabled`]: !isEnabled && !isSelected,
            })}
          >
            {day}
          </div>
          {priceForThisNight
          && (
            <div className="legal-1">
              {priceForThisNight}
            </div>
          )}
        </div>
      </div>
    </CalendarDayMinNightsTooltip>
  );
};

CalendarDay.propTypes = {
  year: PropTypes.number.isRequired,
  month: PropTypes.number.isRequired,
  day: PropTypes.number.isRequired,
};

export default CalendarDay;
